import {useEffect, useState} from 'react';
import {Clipboard} from 'react-native';
import ChessRestClient from '@/Api/ChessRestClient';
import useUpdateVisibility from '@/Hooks/StudyGamesListApi/useUpdateVisibility';
import useRenameStudy from '@/Hooks/StudyGamesListApi/useRenameStudy';
import {STARTING_FEN} from '@/Utils/CommonUtils';

type UseStudySettingsArgs = {
  gameId: any;
  studyDetails: any;
  selectedChapter: any;
  moveSelected: any;
  refetch: () => void;
  setLoading: (loading: boolean) => void;
  showToast: (message: string, opts: {type: string}) => void;
};

// Workbook-level settings: name/description editing, visibility toggle,
// share links, and PGN export — the state and handlers behind the Settings
// tab's Share & Export section, matching Lichess's study settings form.
export function useStudySettings({
  gameId,
  studyDetails,
  selectedChapter,
  moveSelected,
  refetch,
  setLoading,
  showToast,
}: UseStudySettingsArgs) {
  const [editStudyName, setEditStudyName] = useState('');
  const [editStudyDescription, setEditStudyDescription] = useState('');
  useEffect(() => {
    setEditStudyName(studyDetails?.data?.name || '');
    setEditStudyDescription(studyDetails?.data?.description || '');
  }, [studyDetails?.data?.name, studyDetails?.data?.description]);

  const {mutate: renameStudy, isLoading: isSavingStudySettings} =
    useRenameStudy(
      () => {
        refetch();
        showToast('Workbook settings saved', {type: 'Success'});
      },
      () => showToast('Failed to save workbook settings', {type: 'Error'}),
    );

  const handleSaveStudySettings = () => {
    const trimmedName = editStudyName.trim();
    if (!trimmedName) {
      showToast('Workbook name cannot be empty', {type: 'Error'});
      return;
    }
    renameStudy({gameId, name: trimmedName, description: editStudyDescription});
  };

  const {mutate: updateVisibility, isLoading: isUpdatingVisibility} =
    useUpdateVisibility(
      () => {
        refetch();
        showToast('Visibility updated', {type: 'Success'});
      },
      () => showToast('Failed to update visibility', {type: 'Error'}),
    );

  const handleToggleVisibility = () => {
    const nextVisibility =
      studyDetails?.data?.visibility === 'private' ? 'public' : 'private';
    updateVisibility({gameId, visibility: nextVisibility});
  };

  const handleCopyShareLink = () => {
    Clipboard.setString(window.location.href);
    alert('Study link copied to clipboard!');
  };

  const handleCopyFen = () => {
    const fen = moveSelected?.fen || selectedChapter?.fen || STARTING_FEN;
    Clipboard.setString(fen);
    alert('FEN copied to clipboard!');
  };

  const handleExportPgn = async () => {
    if (!selectedChapter?.id) {
      alert('Please select a chapter first.');
      return;
    }
    try {
      setLoading(true);
      const response = await ChessRestClient.get(
        `/learning/study-chapter/generate-pgn/${selectedChapter.id}`,
      );
      if (response.data?.success && response.data?.data?.pgn_content) {
        const pgnContent = response.data.data.pgn_content;
        const blob = new Blob([pgnContent], {type: 'text/plain;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedChapter.name || 'chapter'}.pgn`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PGN content.');
      }
    } catch (e: any) {
      alert(`Error exporting PGN: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  // Matches Lichess's study-level "Download all games" export - every
  // active chapter concatenated into one multi-game PGN file.
  const handleExportStudyPgn = async () => {
    if (!gameId) {
      return;
    }
    try {
      setLoading(true);
      const response = await ChessRestClient.get(
        `/learning/study_games/${gameId}/export-pgn/`,
      );
      if (response.data?.success && response.data?.data?.pgn_content) {
        const pgnContent = response.data.data.pgn_content;
        const blob = new Blob([pgnContent], {type: 'text/plain;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${studyDetails?.data?.name || 'workbook'}.pgn`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PGN content.');
      }
    } catch (e: any) {
      alert(`Error exporting workbook PGN: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    editStudyName,
    setEditStudyName,
    editStudyDescription,
    setEditStudyDescription,
    isSavingStudySettings,
    handleSaveStudySettings,
    isUpdatingVisibility,
    handleToggleVisibility,
    handleCopyShareLink,
    handleCopyFen,
    handleExportPgn,
    handleExportStudyPgn,
  };
}
